import {
  Close,
  ArrowBack,
  ArrowForward,
  WarningAmber,
  OpenInNew,
  HelpOutline
} from '@mui/icons-material'
import {
  Card,
  CardContent,
  Modal,
  Typography,
  IconButton,
  Box,
  useTheme,
  Tooltip,
  styled
} from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { useXRayAnnotations } from '../context/XRayAnnotationsContext'
import { TranscriptionInput } from 'design-system/input'
import { DisagreeButton } from '../../common/DisagreeButton'
import {
  getResultConfigByLabel,
  formatPredictiveValue
} from 'pages/Diagnostics/constants'
import ZoomableImage from 'components/ZoomableImage'
import chestXrayPathologiesDescriptions from '../source/pathologies_descriptions'
import { resultDisclaimer } from '../../common/disclaimer_text'

interface IXRayDiagnosticResultModal {
  title: string
  image: string
  labelResultValue?: 0 | 1 | 2
  ppvResultValue?: number
  npvResultValue?: number
  hideResult?: boolean
  loading?: boolean
  imageId?: string
  isOpen: boolean
  onClose: () => void
  onDisagree?: () => void | Promise<void>
  onDisagreeSuccess?: () => void
  isDisagreed?: boolean
  onNext?: () => void
  onPrevious?: () => void
  hasNext?: boolean
  hasPrevious?: boolean
  showArrowButtons?: boolean
  /** Camada sobre a imagem, repassada ao `ZoomableImage` (acompanha zoom e pan). */
  overlay?: ReactNode
  /** Conteúdo extra no painel, logo acima das anotações. */
  sidePanel?: ReactNode
  /** Conteúdo extra logo abaixo da imagem. */
  bottomPanel?: ReactNode
}

const SourceLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  '&:hover': {
    textDecoration: 'underline',
    '& svg': {
      transform: 'translateX(2px)'
    }
  },
  '& svg': {
    fontSize: '0.875rem',
    transition: 'transform 0.2s ease-in-out'
  }
}))

const XRayDiagnosticResultModal: React.FC<IXRayDiagnosticResultModal> = ({
  title,
  image,
  labelResultValue,
  ppvResultValue,
  npvResultValue,
  hideResult = false,
  imageId = 'default',
  isOpen,
  onClose,
  onDisagree,
  onDisagreeSuccess,
  isDisagreed,
  loading,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
  showArrowButtons = true,
  overlay,
  sidePanel,
  bottomPanel
}) => {
  const { getAnnotation, addAnnotation } = useXRayAnnotations()
  const [annotation, setAnnotation] = useState('')
  const theme = useTheme()

  // Load existing annotation when the component mounts or imageId changes
  useEffect(() => {
    setAnnotation(getAnnotation(imageId))
  }, [imageId, getAnnotation])

  const handleAnnotationChange = (value: string) => {
    setAnnotation(value)
  }

  // A anotação fica em estado local enquanto o usuário digita e é persistida no
  // contexto em toda saída do modal: fechar, avançar ou voltar. Sem isso, trocar
  // de análise descarta o texto, pois o efeito acima recarrega o valor do contexto.
  const saveAnnotation = () => {
    addAnnotation(imageId, annotation)
  }

  const handleClose = () => {
    saveAnnotation()
    onClose()
  }

  const handleNext = () => {
    saveAnnotation()
    onNext?.()
  }

  const handlePrevious = () => {
    saveAnnotation()
    onPrevious?.()
  }

  // Navega entre análises com as setas do teclado (espelha os botões laterais).
  // Ignora quando o foco está num campo editável, onde as setas movem o cursor.
  useEffect(() => {
    if (!isOpen || !showArrowButtons) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

      const target = event.target as HTMLElement | null
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      ) {
        return
      }

      if (event.key === 'ArrowLeft' && hasPrevious) {
        event.preventDefault()
        handlePrevious()
      } else if (event.key === 'ArrowRight' && hasNext) {
        event.preventDefault()
        handleNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  })

  const resultConfig = getResultConfigByLabel(labelResultValue)

  // Predictive value shown in the modal. O lado vem do próprio payload: o backend preenche
  // `ppv_result_value` numa predição positiva e `npv_result_value` numa negativa, nunca os dois.
  // Ler daí (em vez de comparar `result_value` com um threshold fixo) mantém o modal correto
  // quando o threshold de decisão do modelo muda.
  const isPathologySide = ppvResultValue != null
  const predictiveLabel = isPathologySide
    ? 'Valor preditivo positivo (PPV)'
    : 'Valor preditivo negativo (NPV)'
  const predictiveValue = formatPredictiveValue(
    isPathologySide ? ppvResultValue : npvResultValue
  )

  const predictiveDisclaimer = isPathologySide ? (
    <Typography variant='body2'>
      Quando o modelo prediz positivo, PPV mede com qual frequência ele inferiu
      corretamente em testes com 4.305 pacientes e 14.777 imagens. <br />
      <br /> <b>Fórmula:</b> <br />
      PPV = TP / (TP + FP)
    </Typography>
  ) : (
    <Typography variant='body2'>
      Quando o modelo prediz negativo, NPV mede com qual frequência ele inferiu
      corretamente em testes com 4.305 pacientes e 14.777 imagens. <br />
      <br /> <b>Fórmula:</b> <br /> NPV = TN / (TN + FN)
    </Typography>
  )

  const zoomableImage = (
    <ZoomableImage key={image} image={image} alt={title} overlay={overlay} />
  )

  const chestXrayPathologiesDescription =
    chestXrayPathologiesDescriptions[
      imageId as keyof typeof chestXrayPathologiesDescriptions
    ] ?? null

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        outline: 'none',
        p: { xs: 1, sm: 2 },
        overflow: 'auto',
        '& .MuiBackdrop-root': {
          outline: 'none'
        }
      }}
    >
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        width='100%'
        gap={{ xs: 1, sm: 2 }}
        sx={{ outline: 'none', '&:focus-visible': { outline: 'none' } }}
      >
        {/* Coluna 1: Arrow Esquerda */}
        {showArrowButtons && (
          <Box
            flex='0 0 auto'
            display='flex'
            justifyContent='center'
            alignItems='center'
          >
            <IconButton
              onClick={handlePrevious}
              disabled={!hasPrevious}
              sx={{
                zIndex: 10,
                width: { xs: 20, sm: 48 },
                height: { xs: 20, sm: 48 },
                background: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.7)'
                },
                fontSize: { xs: 20, sm: 24 }
              }}
            >
              <ArrowBack />
            </IconButton>
          </Box>
        )}

        {/* Coluna 2: Card com Conteúdo */}
        <Card
          onClick={e => e.stopPropagation()}
          sx={{
            bgcolor: 'tabBorder.main',
            border:
              !hideResult && resultConfig
                ? `2px solid ${resultConfig.color} !important`
                : `2px solid ${theme.palette.common.white} !important`,
            cursor: 'default',
            position: 'relative',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flex: '1 1 auto',
            width: { xs: '100%', sm: '95%', md: '90%' },
            height: { xs: '100%', sm: '95%', md: '90%' },
            maxHeight: { xs: '100vh', sm: '95vh' },
            paddingInline: 4,
            overflow: 'auto',
            outline: 'none',
            '&:focus': {
              outline: 'none'
            },
            '&:focus-visible': {
              outline: 'none'
            }
          }}
        >
          <IconButton
            aria-label='Fechar'
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: { xs: 4, sm: 8 },
              right: { xs: 4, sm: 8 },
              zIndex: 10,
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 }
            }}
          >
            <Close />
          </IconButton>

          {/* Image */}
          <Box
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              display: 'flex',
              flexDirection: 'column',
              // Com o painel, a imagem mantém a altura cheia e os controles ficam abaixo, alcançados
              // rolando o card; centralizado, o topo da imagem sairia da área de rolagem.
              justifyContent: bottomPanel ? 'flex-start' : 'center',
              alignItems: 'center',
              maxHeight: '100%',
              minHeight: { xs: '200px', md: 'auto' },
              position: 'relative'
            }}
          >
            {bottomPanel ? (
              <>
                <Box sx={{ flexShrink: 0, height: '100%', width: '100%' }}>
                  {zoomableImage}
                </Box>
                <Box sx={{ width: '100%', flexShrink: 0, pb: 2 }}>
                  {bottomPanel}
                </Box>
              </>
            ) : (
              zoomableImage
            )}
          </Box>

          {/* Rest of the content */}
          <CardContent
            sx={{
              flex: 1,
              display: 'flex',
              gap: 3,
              flexDirection: 'column',
              p: { sm: 0, md: 3 },
              justifyContent: 'space-between'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Seção 1: Título + Input */}
            <Box>
              <Typography
                variant='h6'
                component='div'
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                {title}
              </Typography>

              {!hideResult && resultConfig && (
                <Box
                  display='flex'
                  flexDirection={{ sm: 'column', md: 'row' }}
                  justifyContent='space-between'
                  alignItems={{ sm: 'flex-start', md: 'center' }}
                  mb={2}
                  gap={1}
                >
                  <Typography
                    variant='body1'
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    gap={1}
                    sx={{
                      color: resultConfig.color
                    }}
                  >
                    {resultConfig.icon} {resultConfig.label} (
                    {resultConfig.risk.toLowerCase()} risco)
                  </Typography>
                  {!hideResult && predictiveValue && (
                    <Box display='flex' alignItems='center' gap={1}>
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        sx={{ width: '120px' }}
                        textAlign={{ xs: 'center', sm: 'left' }}
                      >
                        {predictiveLabel}:
                      </Typography>
                      <Typography variant='h3' color={resultConfig.color}>
                        <strong>{predictiveValue}</strong>
                      </Typography>
                      <Tooltip
                        title={predictiveDisclaimer}
                        placement='top-start'
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'preventOverflow',
                                options: { padding: 8 }
                              },
                              {
                                name: 'flip',
                                enabled: true
                              }
                            ],
                            sx: {
                              '& .MuiTooltip-tooltip': {
                                maxWidth: 400,
                                padding: '8px 12px',
                                backgroundColor: resultConfig.color,
                                color: theme.palette.getContrastText(
                                  resultConfig.color
                                ),
                                fontSize: '0.7rem'
                              }
                            }
                          }
                        }}
                      >
                        <IconButton
                          aria-label='Ajuda sobre valor preditivo (PPV/NPV)'
                          size='small'
                          sx={{
                            color: resultConfig.color,
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <HelpOutline sx={{ fontSize: '1.2rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              )}
              {sidePanel}
              <Box mt={2}>
                <TranscriptionInput
                  label={`Anotações Médicas - ${title}`}
                  placeholder='Digite suas observações sobre esta imagem...'
                  value={annotation}
                  onChange={e => handleAnnotationChange(e.target.value)}
                  rows={8}
                />
              </Box>
            </Box>

            {/* Seção 2: Textos Informativos */}
            {!hideResult && !!chestXrayPathologiesDescription && (
              <Box>
                <Typography variant='h6' color='textPrimary' mb={1}>
                  Identificação de {title}
                </Typography>
                <Typography variant='body2' color='textPrimary' mb={2}>
                  {chestXrayPathologiesDescription.description}
                </Typography>
                <Typography variant='h6' color='textPrimary' mb={1}>
                  Como identificar no Raio-X?
                </Typography>
                <Typography variant='body2' color='textPrimary'>
                  {chestXrayPathologiesDescription.identification}
                </Typography>
                <br />
                <Typography
                  variant='body2'
                  color='textPrimary'
                  mb={1}
                  component='div'
                >
                  Fontes:
                  <ul>
                    {[
                      {
                        url: chestXrayPathologiesDescription.sources
                          .nih_medlineplus,
                        label: 'NIH MedlinePlus'
                      },
                      {
                        url: chestXrayPathologiesDescription.sources
                          .radiopaedia,
                        label: 'Radiopaedia'
                      },
                      {
                        url: chestXrayPathologiesDescription.sources
                          .fleischner_society,
                        label: 'Fleischner Society'
                      },
                      {
                        url: chestXrayPathologiesDescription.sources
                          .radiology_assistant,
                        label: 'Radiology Assistant'
                      },
                      {
                        url: chestXrayPathologiesDescription.sources.oms_who,
                        label: 'OMS/WHO'
                      }
                    ].map(
                      item =>
                        item.url && (
                          <li key={item.label}>
                            <SourceLink
                              href={item.url}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              {item.label}
                              <OpenInNew />
                            </SourceLink>
                          </li>
                        )
                    )}{' '}
                  </ul>
                </Typography>
              </Box>
            )}

            {/* Seção 3: Botão de Discordar */}
            {isDisagreed == undefined ? null : (
              <Box>
                <Box
                  mb={2}
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  gap={1}
                  onClick={e => e.stopPropagation()}
                >
                  <DisagreeButton
                    isDisagreed={isDisagreed}
                    onClick={async () => {
                      if (onDisagree) {
                        await onDisagree()
                      }
                    }}
                    disabled={false}
                    loading={loading}
                    onSuccess={onDisagreeSuccess}
                    sx={{
                      p: { xs: '8px 12px', sm: '5px' },
                      width: { xs: '120px', sm: '100px' },
                      gap: '1px',
                      minHeight: { xs: '42px', sm: 'auto' }
                    }}
                  />

                  <Tooltip
                    title={resultDisclaimer}
                    placement='top-start'
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'preventOverflow',
                            options: { padding: 8 }
                          },
                          {
                            name: 'flip',
                            enabled: true
                          }
                        ],
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            maxWidth: 400,
                            padding: '8px 12px',
                            backgroundColor: theme.palette.warning.main,
                            color: theme.palette.warning.contrastText,
                            fontSize: '0.7rem'
                          }
                        }
                      }
                    }}
                  >
                    <IconButton
                      size='small'
                      sx={{
                        color: 'warning.main',
                        alignSelf: 'flex-start'
                      }}
                    >
                      <WarningAmber />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Coluna 3: Arrow Direita */}
        {showArrowButtons && (
          <Box
            flex='0 0 auto'
            display='flex'
            justifyContent='center'
            alignItems='center'
          >
            <IconButton
              onClick={handleNext}
              disabled={!hasNext}
              sx={{
                zIndex: 10,
                width: { xs: 20, sm: 48 },
                height: { xs: 20, sm: 48 },
                background: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.7)'
                },
                fontSize: { xs: 20, sm: 24 }
              }}
            >
              <ArrowForward />
            </IconButton>
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default XRayDiagnosticResultModal
